import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Cloud,
  Pencil,
  RefreshCw,
  Save,
  Target,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { SkeletonRows } from '@/components/ui/Skeleton';
import {
  useUpdateVersionTarget,
  useVersionTargets,
  type VersionTargetRow,
} from '@/hooks/useVersionTargets';
import { iconForServiceType } from '@/lib/serviceIcons';
import { formatDate } from '@/lib/format';

/**
 * /settings/version-targets - Soll-Versionen-Editor (Sprint 4, WT-404).
 *
 * Tabelle aller Service-Types (DB-seeded + Admin-angelegte) mit:
 *  - Aktuelle Soll-Version + EOL-Datum
 *  - Anzahl Systeme die diesen Service nutzen
 *  - Anzahl Systeme im Drift gegen diesen Target
 *  - Inline-Edit (target_version + eol_at + notes)
 *
 * "Sync mit Upstream"-Button ist Sprint 5 (Stub) - Idee: latest stable von
 * offiziellen Releases-Feeds (Proxmox, Nextcloud, OPNsense ...).
 */

export const Route = createFileRoute('/settings/version-targets')({
  component: VersionTargetsPage,
});

function VersionTargetsPage(): JSX.Element {
  const targetsQuery = useVersionTargets();
  const rows = useMemo(() => targetsQuery.data?.data ?? [], [targetsQuery.data]);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Target className="size-6 text-primary" aria-hidden="true" />
            <h1 className="text-2xl font-semibold">Soll-Versionen</h1>
          </div>
          <p className="text-muted">
            Pro Service-Type: aktuelle Ziel-Version + EOL-Datum. Die Drift-Logik
            vergleicht Ist vs. Ziel pro System.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="size-4" />}
            onClick={() => void targetsQuery.refetch()}
            loading={targetsQuery.isFetching}
          >
            Aktualisieren
          </Button>
          <Button
            variant="ghost"
            leftIcon={<Cloud className="size-4" />}
            disabled
            title="Noch nicht implementiert - latest stable von offiziellen Release-Feeds"
            data-testid="version-targets-upstream-sync"
          >
            Sync mit Upstream
          </Button>
        </div>
      </header>

      <Card title="Service-Types">
        {targetsQuery.isLoading && <SkeletonRows rows={6} cols={6} />}
        {targetsQuery.isError && <ErrorAlert error={targetsQuery.error} />}
        {!targetsQuery.isLoading && !targetsQuery.isError && rows.length === 0 && (
          <EmptyState
            icon={<Target className="size-8" />}
            title="Keine Service-Types"
            description="Service-Types werden im Inventar (.ods-Import) gepflegt. Erst nach Import erscheinen hier Einträge."
          />
        )}
        {rows.length > 0 && <VersionTargetsTable rows={rows} />}
      </Card>
    </div>
  );
}

interface VersionTargetsTableProps {
  rows: VersionTargetRow[];
}

interface VersionTargetDraft {
  targetVersion: string;
  eolAt: string;
  notes: string;
}

function draftFromRow(row: VersionTargetRow): VersionTargetDraft {
  return {
    targetVersion: row.target_version ?? '',
    eolAt: toDateInputValue(row.eol_at),
    notes: row.notes ?? '',
  };
}

export function VersionTargetsTable({ rows }: VersionTargetsTableProps): JSX.Element {
  const [drafts, setDrafts] = useState<Record<string, VersionTargetDraft>>({});
  const update = useUpdateVersionTarget();

  const columns = useMemo((): ReadonlyArray<Column<VersionTargetRow>> => {
    const startEdit = (row: VersionTargetRow): void => {
      setDrafts((prev) => ({ ...prev, [row.service_type]: draftFromRow(row) }));
    };
    const stopEdit = (serviceType: string): void => {
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[serviceType];
        return next;
      });
    };
    const patchDraft = (serviceType: string, patch: Partial<VersionTargetDraft>): void => {
      setDrafts((prev) => {
        const current = prev[serviceType];
        if (!current) return prev;
        return { ...prev, [serviceType]: { ...current, ...patch } };
      });
    };
    const submit = (row: VersionTargetRow): void => {
      const draft = drafts[row.service_type];
      if (!draft || !draft.targetVersion.trim()) return;
      update.mutate(
        {
          serviceType: row.service_type,
          targetVersion: draft.targetVersion.trim(),
          eolAt: draft.eolAt ? `${draft.eolAt}T00:00:00Z` : null,
          notes: draft.notes.trim() ? draft.notes.trim() : null,
        },
        { onSuccess: () => stopEdit(row.service_type) },
      );
    };

    return [
      {
        id: 'service-type',
        header: 'Service-Type',
        filterValue: (row) =>
          [row.service_type_label ?? row.service_type, row.service_type].join(' '),
        cell: (row) =>
          drafts[row.service_type] ? (
            <>
              <span className="font-medium">{row.service_type_label ?? row.service_type}</span>
              <span className="ml-1 font-mono text-xs text-muted">{row.service_type}</span>
            </>
          ) : (
            <ViewServiceTypeCell row={row} />
          ),
      },
      {
        id: 'soll-version',
        header: 'Soll-Version',
        filterValue: (row) => row.target_version ?? '',
        cell: (row) => {
          const draft = drafts[row.service_type];
          if (draft) {
            return (
              <Input
                value={draft.targetVersion}
                onChange={(e) =>
                  patchDraft(row.service_type, { targetVersion: e.target.value })
                }
                placeholder="z.B. 24.7.4"
                data-testid={`vt-input-version-${row.service_type}`}
              />
            );
          }
          return (
            <code className="rounded bg-surface-2 px-1 py-0.5 text-xs">
              {row.target_version ?? '–'}
            </code>
          );
        },
      },
      {
        id: 'eol',
        header: 'EOL',
        filterValue: (row) => {
          if (!row.eol_at) return '';
          const label = formatDate(row.eol_at);
          return label === '–' ? '' : label;
        },
        cell: (row) => {
          const draft = drafts[row.service_type];
          if (draft) {
            return (
              <input
                type="date"
                value={draft.eolAt}
                onChange={(e) => patchDraft(row.service_type, { eolAt: e.target.value })}
                className="w-full rounded-md border border-border bg-surface px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid={`vt-input-eol-${row.service_type}`}
              />
            );
          }
          return <ViewEolCell row={row} />;
        },
      },
      {
        id: 'systeme',
        header: 'Systeme',
        headerClassName: 'text-right',
        className: 'text-right tabular-nums',
        filterValue: (row) => String(row.systems_total),
        cell: (row) => row.systems_total,
      },
      {
        id: 'drift',
        header: 'davon Drift',
        headerClassName: 'text-right',
        className: 'text-right tabular-nums',
        filterValue: (row) => String(row.systems_drifted),
        cell: (row) => {
          if (drafts[row.service_type]) {
            return row.systems_drifted;
          }
          return row.systems_drifted > 0 ? (
            <Badge tone={row.systems_drifted > 5 ? 'danger' : 'warning'}>
              {row.systems_drifted}
            </Badge>
          ) : (
            <span className="text-muted">0</span>
          );
        },
      },
      {
        id: 'notizen',
        header: 'Notizen',
        filterValue: (row) => row.notes ?? '',
        cell: (row) => {
          const draft = drafts[row.service_type];
          if (draft) {
            return (
              <Input
                value={draft.notes}
                onChange={(e) => patchDraft(row.service_type, { notes: e.target.value })}
                placeholder="z.B. EOL-Hinweis"
                data-testid={`vt-input-notes-${row.service_type}`}
              />
            );
          }
          return (
            <span
              className="max-w-[280px] truncate text-xs text-muted"
              title={row.notes ?? ''}
            >
              {row.notes ?? '–'}
            </span>
          );
        },
      },
      {
        id: 'aktion',
        header: 'Aktion',
        filter: false,
        headerClassName: 'text-right',
        className: 'text-right',
        cell: (row) => {
          const draft = drafts[row.service_type];
          if (draft) {
            return (
              <>
                <div className="flex justify-end gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<X className="size-3" />}
                    onClick={() => stopEdit(row.service_type)}
                    disabled={update.isPending}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={<Save className="size-3" />}
                    onClick={() => submit(row)}
                    loading={update.isPending}
                    disabled={!draft.targetVersion.trim()}
                    data-testid={`vt-save-${row.service_type}`}
                  >
                    Speichern
                  </Button>
                </div>
                {update.isError && (
                  <div className="mt-1">
                    <span className="inline-flex items-center gap-1 text-xs text-danger">
                      <AlertTriangle className="size-3" aria-hidden="true" />
                      {update.error instanceof Error
                        ? update.error.message
                        : 'Fehler beim Speichern'}
                    </span>
                  </div>
                )}
              </>
            );
          }
          return (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<Pencil className="size-3" />}
              onClick={() => startEdit(row)}
              data-testid={`vt-edit-${row.service_type}`}
            >
              Edit
            </Button>
          );
        },
      },
    ];
  }, [drafts, update]);

  return (
    <DataTable
      caption="Soll-Versionen pro Service-Type"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.service_type}
      storageKey="soll-versionen"
      rowTestId={(row) =>
        drafts[row.service_type]
          ? `vt-row-${row.service_type}-editing`
          : `vt-row-${row.service_type}`
      }
    />
  );
}

function ViewServiceTypeCell({ row }: { row: VersionTargetRow }): JSX.Element {
  const Icon = iconForServiceType(row.service_type);
  return (
    <span className="inline-flex items-center gap-2">
      <Icon className="size-4 text-muted" aria-hidden="true" />
      <span className="font-medium">{row.service_type_label ?? row.service_type}</span>
      <span className="font-mono text-xs text-muted">{row.service_type}</span>
    </span>
  );
}

function ViewEolCell({ row }: { row: VersionTargetRow }): JSX.Element {
  const eolPast = isEolPast(row.eol_at);
  if (!row.eol_at) {
    return <span className="text-xs text-muted">–</span>;
  }
  return (
    <span
      className={['flex flex-col text-xs', eolPast ? 'text-danger' : 'text-text'].join(' ')}
      data-testid={`vt-eol-${row.service_type}`}
    >
      <RelativeTime value={row.eol_at} />
      <span className="text-muted">{formatDate(row.eol_at)}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // YYYY-MM-DD im ISO-UTC, ohne TZ-Konvertierung (sonst springt der EOL-Tag).
  return d.toISOString().slice(0, 10);
}

export function isEolPast(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}
